import { CognitoUserPool, type CognitoUserSession } from 'amazon-cognito-identity-js';
import { User } from 'oidc-client-ts';

export function createCognitoUserPool(config: QualityVetRuntimeConfig): CognitoUserPool {
  const userPoolId = new URL(config.cognitoAuthority).pathname.split('/').filter(Boolean).at(-1);
  if (!userPoolId || !config.cognitoClientId) throw new Error('Cognito is not configured.');
  return new CognitoUserPool({ UserPoolId: userPoolId, ClientId: config.cognitoClientId });
}

export function currentCognitoSession(pool: CognitoUserPool): Promise<CognitoUserSession | null> {
  const currentUser = pool.getCurrentUser();
  if (!currentUser) return Promise.resolve(null);

  return new Promise((resolve) =>
    currentUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session?.isValid()) {
        currentUser.signOut();
        resolve(null);
        return;
      }
      resolve(session);
    }),
  );
}

export function jwtPayload(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];
  if (!payload) throw new Error('Cognito returned an invalid token.');
  const encodedPayload = payload.replaceAll('-', '+').replaceAll('_', '/');
  const decodedPayload = decodeURIComponent(
    atob(encodedPayload)
      .split('')
      .map((character) => `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  );
  const parsed = JSON.parse(decodedPayload) as unknown;
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('Cognito returned an invalid token payload.');
  }
  return parsed as Record<string, unknown>;
}

export function profileFromIdToken(token: string): User['profile'] {
  return jwtPayload(token) as User['profile'];
}

export function userFromCognitoSession(session: CognitoUserSession): User {
  const idToken = session.getIdToken().getJwtToken();
  const refreshToken = session.getRefreshToken().getToken();
  return new User({
    id_token: idToken,
    access_token: session.getAccessToken().getJwtToken(),
    ...(refreshToken ? { refresh_token: refreshToken } : {}),
    token_type: 'Bearer',
    scope: 'openid email profile',
    profile: profileFromIdToken(idToken),
    expires_at: session.getAccessToken().getExpiration(),
  });
}

export function cognitoError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return new Error(error.message);
  }
  return new Error('Cognito could not complete the request.');
}
